import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sun, Moon, User, LogOut, Menu } from 'lucide-react';
import { USER_MENU_ITEMS } from '../lib/nav';

function safeGetItem(key, fallback = null) {
  try { return localStorage.getItem(key); } catch { return fallback; }
}

function safeSetItem(key, value) {
  try { localStorage.setItem(key, value); } catch { /* storage unavailable */ }
}

function safeRemoveItem(key) {
  try { localStorage.removeItem(key); } catch { /* storage unavailable */ }
}

export default function Header({ onMenu }) {
  const [dark, setDark] = useState(() => safeGetItem('cts_theme') === 'dark');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    safeSetItem('cts_theme', dark ? 'dark' : 'light');
  }, [dark]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);

  const handleLogout = () => {
    safeRemoveItem('mickey_token');
    window.location.href = '/login';
  };

  return (
    <header className="h-14 bg-white dark:bg-slate-900 border-b border-[var(--border)] flex items-center justify-between px-3 sm:px-6 shrink-0 z-10">
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        {onMenu && (
          <button onClick={onMenu} className="-ml-1 rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-slate-800 md:hidden" aria-label="Open menu">
            <Menu size={20} />
          </button>
        )}
        <span className="truncate text-lg font-bold text-primary sm:text-xl">{import.meta.env.VITE_TENANT_NAME || 'Tenant'}</span>
        <span className="text-sm text-[var(--text-secondary)] hidden sm:inline">{import.meta.env.VITE_TENANT_TAGLINE || ''}</span>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setDark(!dark)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
          title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {dark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <div className="relative">
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
          >
            <User size={18} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-slate-900 border border-[var(--border)] rounded-lg shadow-lg py-1 z-50">
              {USER_MENU_ITEMS.map(({ to, label, icon: Icon }) => (
                <Link key={to} to={to} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center gap-2">
                  <Icon size={14} /> {label}
                </Link>
              ))}
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center gap-2"
              >
                <LogOut size={14} /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
